import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, useUser } from "@clerk/react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useDuckDB } from "../hooks/useDuckDB";
import {
  FolderOpen, LogOut, Trash2, Search, RefreshCw,
  X, Pencil, Activity, Settings, Eye, Mail,
  UserPlus, Clock, CheckCircle, ChevronDown, ExternalLink, Plus,
  Upload, Layers, Inbox, Check, Globe,
} from "lucide-react";

const API  = process.env.REACT_APP_API_URL || "";
const font = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

// ── tokens ───────────────────────────────────────────────────────────────────
const T = {
  nav:         "#232231",
  navBorder:   "#2d2c3f",
  navText:     "#9ca3af",
  navActive:   "#f9fafb",
  bg:          "#f9fafb",
  surface:     "#ffffff",
  border:      "#e5e7eb",
  text:        "#111827",
  muted:       "#6b7280",
  accent:      "#232231",
  accentLight: "#eeeef2",
  accentText:  "#232231",
  warn:        "#d97706",
  danger:      "#dc2626",
  dangerLight: "#fee2e2",
  shadow:      "0 1px 2px rgba(0,0,0,0.06)",
  shadowMd:    "0 4px 16px rgba(0,0,0,0.08)",
  r:           "8px",
  rLg:         "12px",
};

const inp = {
  display:"block", width:"100%", boxSizing:"border-box",
  padding:"8px 11px", fontSize:14, lineHeight:1.5,
  border:`1px solid ${T.border}`, borderRadius:T.r,
  background:T.surface, color:T.text, outline:"none", fontFamily:font,
};

const lbl = {
  display:"block", fontSize:12, fontWeight:600, color:T.muted,
  marginBottom:5, letterSpacing:"0.02em", textTransform:"uppercase", fontFamily:font,
};

const btnPrimary = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"8px 16px", fontSize:13, fontWeight:600,
  background:T.accent, color:"#fff",
  border:"none", borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

const btnGhost = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"7px 13px", fontSize:13, fontWeight:500,
  background:"transparent", color:"#232231",
  border:"1px solid #232231", borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

const btnDanger = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"7px 13px", fontSize:13, fontWeight:500,
  background:T.dangerLight, color:T.danger,
  border:`1px solid #fca5a5`, borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

// ── responsive hook ───────────────────────────────────────────────────────────
function useIsMobile(breakpoint = 680) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = e => setMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return mobile;
}

// ── small helpers ─────────────────────────────────────────────────────────────
function Tag({ children, color = T.accentText, bg = T.accentLight, border = "#c4c4d0" }) {
  return (
    <span style={{ display:"inline-block", padding:"2px 9px", borderRadius:999,
      background:bg, color, border:`1px solid ${border}`,
      fontSize:11, fontWeight:700, fontFamily:font, letterSpacing:"0.03em" }}>
      {children}
    </span>
  );
}

function RoleBadge({ role }) {
  if (role === "admin")
    return <Tag bg="#fef3c7" color="#92400e" border="#fde68a">Admin</Tag>;
  return <Tag>User</Tag>;
}

function Avatar({ name, size = 28 }) {
  const ch = (name?.[0] || "?").toUpperCase();
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", flexShrink:0,
      background:T.accent, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ color:"#fff", fontSize:size * 0.42, fontWeight:700, fontFamily:font }}>{ch}</span>
    </div>
  );
}

// ── metric strip ──────────────────────────────────────────────────────────────
function MetricStrip({ items }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display:"flex", flexWrap: isMobile ? "wrap" : "nowrap",
      background:T.surface, border:`1px solid ${T.border}`,
      borderRadius:T.rLg, overflow:"hidden", boxShadow:T.shadow, marginBottom:28,
    }}>
      {items.map((m, i) => (
        <div key={i} style={{
          flex: isMobile ? "1 1 50%" : 1,
          padding: isMobile ? "14px 16px" : "18px 22px",
          borderRight: !isMobile && i < items.length - 1 ? `1px solid ${T.border}` : "none",
          borderBottom: isMobile && i < items.length - 2 ? `1px solid ${T.border}` : "none",
          boxSizing:"border-box",
        }}>
          <p style={{ margin:0, fontSize:11, fontWeight:600, color:T.muted,
            textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:font }}>{m.label}</p>
          <p style={{ margin:"6px 0 0", fontSize: isMobile ? 22 : 26, fontWeight:700, color:T.text,
            lineHeight:1, fontFamily:font }}>{m.value ?? "—"}</p>
          {m.sub && <p style={{ margin:"4px 0 0", fontSize:12, color:T.muted, fontFamily:font }}>{m.sub}</p>}
        </div>
      ))}
    </div>
  );
}

// ── section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, desc, actions }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ display:"flex", alignItems:"flex-start",
      flexDirection: isMobile && actions ? "column" : "row",
      justifyContent:"space-between", marginBottom:20, gap:12 }}>
      <div>
        <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:T.text, fontFamily:font }}>{title}</h2>
        {desc && <p style={{ margin:"3px 0 0", fontSize:13, color:T.muted, fontFamily:font }}>{desc}</p>}
      </div>
      {actions && (
        <div style={{ display:"flex", gap:8, flexShrink:0, flexWrap:"wrap" }}>{actions}</div>
      )}
    </div>
  );
}

// ── table ─────────────────────────────────────────────────────────────────────
function Table({ cols, rows, loading, empty }) {
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.rLg,
      overflow:"hidden", boxShadow:T.shadow }}>
      <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, fontFamily:font, minWidth:400 }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${T.border}` }}>
              {cols.map(c => (
                <th key={c.key} style={{ padding:"10px 16px", textAlign:"left",
                  fontSize:11, fontWeight:700, color:T.muted, whiteSpace:"nowrap",
                  letterSpacing:"0.06em", textTransform:"uppercase", background:"#f9fafb" }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols.length} style={{ padding:"40px 16px", textAlign:"center", color:T.muted }}>
                Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cols.length} style={{ padding:"40px 16px", textAlign:"center", color:T.muted }}>
                {empty || "Nothing here yet."}
              </td></tr>
            ) : rows.map((row, i) => (
              <tr key={row._key ?? i}
                style={{ borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : "none" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {cols.map(c => (
                  <td key={c.key} style={{ padding:"11px 16px", color:T.muted, verticalAlign:"middle" }}>
                    {c.render ? c.render(row) : (row[c.key] ?? <span style={{ color:"#d1d5db" }}>—</span>)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, subtitle, icon: Icon, onClose, children, width = 500 }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,0.4)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={onClose}>
      <div style={{ background:"#fff", borderRadius:T.rLg, width, maxWidth:"100%",
        maxHeight:"90vh", display:"flex", flexDirection:"column",
        boxShadow:"0 20px 60px rgba(0,0,0,0.2)", fontFamily:font }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:"22px 24px 18px", borderBottom:`1px solid ${T.border}`,
          display:"flex", alignItems:"flex-start", gap:14, flexShrink:0 }}>
          {Icon && (
            <div style={{ width:38, height:38, borderRadius:T.r, background:T.accentLight,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Icon size={17} color={T.accent} />
            </div>
          )}
          <div style={{ flex:1 }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:T.text }}>{title}</h3>
            {subtitle && <p style={{ margin:"2px 0 0", fontSize:13, color:T.muted }}>{subtitle}</p>}
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"none", cursor:"pointer",
              color:T.muted, padding:4, lineHeight:0 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding:"20px 24px", overflowY:"auto", flex:1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── ConfirmModal ──────────────────────────────────────────────────────────────
function ConfirmModal({ title, body, confirmLabel, onConfirm, onClose, danger = true }) {
  return (
    <Modal title={title} onClose={onClose} width={400}>
      <p style={{ margin:"0 0 22px", fontSize:14, color:T.muted, lineHeight:1.6 }}
        dangerouslySetInnerHTML={{ __html: body }} />
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
        <button onClick={onClose} style={btnGhost}>Cancel</button>
        <button onClick={onConfirm} style={danger ? btnDanger : btnPrimary}>{confirmLabel}</button>
      </div>
    </Modal>
  );
}

// ── access logs ───────────────────────────────────────────────────────────────
function AccessLogsModal({ user, getToken, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(
          `${API}/api/adminUserAccessLogs?userId=${encodeURIComponent(user.email || "")}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || `${res.status}`);
        setLogs(d.logs || []);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [user.email, getToken]);

  return (
    <Modal title="Access logs" subtitle={user.email} icon={Activity} onClose={onClose} width={560}>
      {loading && <p style={{ textAlign:"center", color:T.muted, padding:"20px 0" }}>Loading…</p>}
      {error   && <p style={{ color:T.danger, fontSize:13 }}>{error}</p>}
      {!loading && !error && logs.length === 0 && (
        <p style={{ textAlign:"center", color:T.muted, padding:"20px 0" }}>No login history found.</p>
      )}
      {!loading && logs.length > 0 && (
        <Table
          loading={false} empty=""
          cols={[
            { key:"accessed_at", label:"Date", render:l => l.accessed_at ? new Date(l.accessed_at).toLocaleString() : "—" },
            { key:"ip",          label:"IP",   render:l => <span style={{ fontFamily:"monospace", fontSize:12 }}>{l.ip_location || "—"}</span> },
            { key:"city",        label:"City", render:l => l.city    || "—" },
            { key:"country",     label:"Country", render:l => l.country || "—" },
          ]}
          rows={logs.map((l, i) => ({ ...l, _key:i }))}
        />
      )}
      <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
        <button onClick={onClose} style={btnGhost}>Close</button>
      </div>
    </Modal>
  );
}

// ── invite modal ──────────────────────────────────────────────────────────────
function InviteModal({ onClose, onSuccess, getToken, projects }) {
  const isMobile = useIsMobile();
  const [form, setForm]       = useState({ first_name:"", last_name:"", email:"", affiliation:"", role:"user" });
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const getUserIds = p => {
    const raw = p.user_ids ?? p.client_ids ?? "";
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
  };

  const toggle = id => setChecked(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const submit = async e => {
    e.preventDefault();
    if (!form.email) { setError("Email is required"); return; }
    setError(""); setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/adminUsers`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ ...form, project_ids:[...checked], product:"inspekt" }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `${res.status}`);
      onSuccess();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Invite user" subtitle="An invitation email will be sent automatically"
      icon={UserPlus} onClose={onClose} width={500}>
      <form onSubmit={submit} style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12 }}>
          <div><label style={lbl}>First name</label><input style={inp} value={form.first_name} onChange={set("first_name")} autoFocus /></div>
          <div><label style={lbl}>Last name</label><input style={inp} value={form.last_name} onChange={set("last_name")} /></div>
        </div>
        <div>
          <label style={lbl}>Email <span style={{ color:T.danger }}>*</span></label>
          <input type="email" required style={inp} value={form.email} onChange={set("email")} />
        </div>
        <div><label style={lbl}>Organisation</label><input style={inp} value={form.affiliation} onChange={set("affiliation")} /></div>
        <div>
          <label style={lbl}>Role</label>
          <select style={{ ...inp, cursor:"pointer" }} value={form.role} onChange={set("role")}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {projects.length > 0 && (
          <div>
            <label style={lbl}>Assign to projects</label>
            <div style={{ border:`1px solid ${T.border}`, borderRadius:T.r, padding:10,
              maxHeight:150, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 }}>
              {projects.map(p => (
                <label key={p.id} style={{ display:"flex", alignItems:"center", gap:10,
                  cursor:"pointer", fontSize:13, color:T.text }}>
                  <input type="checkbox" checked={checked.has(p.id)} onChange={() => toggle(p.id)}
                    style={{ accentColor:T.accent, width:14, height:14 }} />
                  <span style={{ flex:1 }}>{p.name}</span>
                  <span style={{ fontSize:11, color:T.muted }}>{getUserIds(p).length} users</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div style={{ padding:"9px 12px", background:T.dangerLight, borderRadius:T.r,
            border:"1px solid #fca5a5", color:T.danger, fontSize:13 }}>{error}</div>
        )}
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", paddingTop:4 }}>
          <button type="button" onClick={onClose} style={btnGhost}>Cancel</button>
          <button type="submit" disabled={loading} style={btnPrimary}>
            <Mail size={13} />{loading ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── edit user modal ───────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSuccess, getToken, projects }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    first_name:user.first_name||"", last_name:user.last_name||"",
    affiliation:user.affiliation||"", role:user.role||"user",
  });
  const getUserIds = p => {
    const raw = p.user_ids ?? p.client_ids ?? "";
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
  };
  const [checked, setChecked] = useState(new Set(
    projects.filter(p => getUserIds(p).includes(user.email || user.id)).map(p => p.id)
  ));
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggle = id => setChecked(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const submit = async e => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/editUser`, {
        method:"PUT",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ id:user.id, ...form, project_ids:[...checked] }),
      });
      const text = await res.text();
      const d = text ? JSON.parse(text) : {};
      if (!res.ok) throw new Error(d.error || `${res.status}`);
      onSuccess();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Edit user" subtitle={user.email} icon={Pencil} onClose={onClose} width={460}>
      <form onSubmit={submit} style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:12 }}>
          <div><label style={lbl}>First name</label><input style={inp} value={form.first_name} onChange={set("first_name")} /></div>
          <div><label style={lbl}>Last name</label><input style={inp} value={form.last_name} onChange={set("last_name")} /></div>
        </div>
        <div><label style={lbl}>Organisation</label><input style={inp} value={form.affiliation} onChange={set("affiliation")} /></div>
        <div>
          <label style={lbl}>Role</label>
          <select style={{ ...inp, cursor:"pointer" }} value={form.role} onChange={set("role")}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {projects.length > 0 && (
          <div>
            <label style={lbl}>Project access</label>
            <div style={{ border:`1px solid ${T.border}`, borderRadius:T.r, padding:10,
              maxHeight:130, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 }}>
              {projects.map(p => (
                <label key={p.id} style={{ display:"flex", alignItems:"center", gap:10,
                  cursor:"pointer", fontSize:13, color:T.text }}>
                  <input type="checkbox" checked={checked.has(p.id)} onChange={() => toggle(p.id)}
                    style={{ accentColor:T.accent, width:14, height:14 }} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div style={{ padding:"9px 12px", background:T.dangerLight, borderRadius:T.r,
            border:"1px solid #fca5a5", color:T.danger, fontSize:13 }}>{error}</div>
        )}
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", paddingTop:4 }}>
          <button type="button" onClick={onClose} style={btnGhost}>Cancel</button>
          <button type="submit" disabled={loading} style={btnPrimary}>{loading ? "Saving…" : "Save changes"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── Layer helpers ─────────────────────────────────────────────────────────────
function parseR2Layers(raw) {
  try {
    const parsed = JSON.parse(raw || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && Array.isArray(parsed.r2)) {
      return new Set(parsed.r2.map(l => l.key));
    }
    return new Set();
  } catch { return new Set(); }
}

// ── Layer map preview panel ────────────────────────────────────────────────────
function LayerPreviewPanel({ layerKey, layerName, getToken, dbRef, dbReady, onClose }) {
  const mapContainerRef  = useRef(null);
  const leafletMapRef    = useRef(null);
  const markersLayerRef  = useRef(null);
  const [status,   setStatus]   = useState("loading");
  const [phase,    setPhase]    = useState("init");
  const [errMsg,   setErrMsg]   = useState("");
  const [totalPts, setTotalPts] = useState(0);

  useEffect(() => {
    if (!dbReady) return;
    let cancelled = false;
    (async () => {
      try {
        setStatus(leafletMapRef.current ? "refreshing" : "loading");
        setPhase("schema");
        const token = await getToken();
        const url   = `${window.location.origin}/api/r2-stream?key=${encodeURIComponent(layerKey)}&t=${encodeURIComponent(token)}`;

        const conn = await dbRef.current.connect();

        const schemaResult = await conn.query(`SELECT * FROM read_parquet('${url}') LIMIT 0`);
        const colMap = {};
        schemaResult.schema.fields.forEach(f => { colMap[f.name.toLowerCase()] = f.name; });
        const latKey = Object.keys(colMap).find(c => /^(lat|latitude|y)$/.test(c));
        const lonKey = Object.keys(colMap).find(c => /^(lon|lng|longitude|x)$/.test(c));
        if (!latKey || !lonKey) throw new Error(
          `No lat/lon columns found. Available: ${Object.values(colMap).join(", ")}`
        );
        const latCol = colMap[latKey];
        const lonCol = colMap[lonKey];

        setPhase("points");
        const result = await conn.query(
          `SELECT "${latCol}", "${lonCol}"
           FROM read_parquet('${url}')
           WHERE "${latCol}" IS NOT NULL AND "${lonCol}" IS NOT NULL`
        );
        await conn.close();

        if (cancelled) return;

        setPhase("render");
        const points = [];
        result.toArray().forEach(row => {
          const lat = Number(row[latCol]);
          const lon = Number(row[lonCol]);
          if (isFinite(lat) && isFinite(lon)) points.push([lat, lon]);
        });

        setTotalPts(points.length);

        // Create map once; canvas renderer handles large point counts
        if (!leafletMapRef.current && mapContainerRef.current) {
          const map = L.map(mapContainerRef.current, {
            zoomControl: false, attributionControl: false, renderer: L.canvas(),
          });
          L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            { maxZoom: 18 }
          ).addTo(map);
          map.setView([20, 0], 2);
          markersLayerRef.current = L.layerGroup().addTo(map);
          leafletMapRef.current = map;
        }

        const map = leafletMapRef.current;
        if (!map) return;

        markersLayerRef.current.clearLayers();
        points.forEach(([lat, lon]) =>
          L.circleMarker([lat, lon], {
            radius: 3, fillColor: "#ef4444", color: "#fff", weight: 0.5, fillOpacity: 0.85,
          }).addTo(markersLayerRef.current)
        );

        map.invalidateSize();
        if (points.length) {
          let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
          for (const [lat, lon] of points) {
            if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
            if (lon < minLon) minLon = lon; if (lon > maxLon) maxLon = lon;
          }
          map.flyToBounds(
            [[minLat, minLon], [maxLat, maxLon]],
            { padding: [24, 24], duration: 0.8 }
          );
        } else {
          map.setView([20, 0], 2);
        }

        setStatus("ready");
      } catch (e) {
        if (!cancelled) { setErrMsg(e.message); setStatus("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [layerKey, getToken, dbRef, dbReady]);

  useEffect(() => () => {
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; }
  }, []);

  const phaseLabel = !dbReady       ? "Initialising DuckDB…"
    : phase === "schema"            ? "Reading schema…"
    : phase === "points"            ? "Loading points…"
    : "Rendering…";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10, height:"100%" }}>
      <style>{`@keyframes preview-slide{0%{transform:translateX(-100%)}60%{transform:translateX(160%)}100%{transform:translateX(160%)}}`}</style>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <span style={{ fontSize:11, fontWeight:600, color:T.muted, fontFamily:font,
          textTransform:"uppercase", letterSpacing:"0.05em" }}>Preview</span>
        <button onClick={onClose} type="button"
          style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, padding:2, lineHeight:0 }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:T.text, fontFamily:font, flex:1,
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {layerName}
        </div>
        {status === "refreshing" && (
          <span style={{ fontSize:11, color:T.muted, fontFamily:font, whiteSpace:"nowrap", flexShrink:0 }}>
            {phaseLabel}
          </span>
        )}
      </div>
      {status === "refreshing" && (
        <div style={{ height:3, background:"#e5e7eb", borderRadius:2, overflow:"hidden", flexShrink:0, marginTop:-6 }}>
          <div style={{ height:"100%", width:"40%", background:"#3b82f6", borderRadius:2,
            animation:"preview-slide 1.4s ease-in-out infinite" }} />
        </div>
      )}
      <div style={{ position:"relative", height:380, flexShrink:0, borderRadius:T.r,
        overflow:"hidden", border:`1px solid ${T.border}` }}>
        {status === "loading" && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center", background:"#f9fafb", zIndex:10, gap:14 }}>
            <span style={{ fontSize:13, color:T.muted, fontFamily:font }}>{phaseLabel}</span>
            <div style={{ width:200, height:4, background:"#e5e7eb", borderRadius:2, overflow:"hidden" }}>
              <div style={{ height:"100%", width:"40%", background:"#3b82f6", borderRadius:2,
                animation:"preview-slide 1.4s ease-in-out infinite" }} />
            </div>
          </div>
        )}
        {status === "error" && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
            justifyContent:"center", background:"#fef2f2", zIndex:10, padding:16 }}>
            <span style={{ fontSize:12, color:T.danger, fontFamily:font, textAlign:"center" }}>{errMsg}</span>
          </div>
        )}
        <div ref={mapContainerRef} style={{ width:"100%", height:"100%" }} />
      </div>
      {(status === "ready" || status === "refreshing") && totalPts > 0 && (
        <div style={{ fontSize:11, color:T.muted, fontFamily:font, flexShrink:0 }}>
          {totalPts.toLocaleString()} points loaded
        </div>
      )}
    </div>
  );
}

// ── project modal (create / edit) ─────────────────────────────────────────────
function ProjectModal({ project, onClose, onSuccess, getToken, users, dbRef, dbReady }) {
  const isEdit = !!project;

  // After getProjects, user_ids is a comma-separated string; parse back to array
  const parseProjectUsers = p => {
    if (!p) return [];
    const raw = p.user_ids ?? "";
    if (Array.isArray(raw)) return raw;
    return raw.split(",").map(s => s.trim()).filter(Boolean);
  };

  const [form, setForm] = useState({
    project_name: project?.name        || "",
    description:  project?.description || "",
  });
  const [checked, setChecked] = useState(new Set(isEdit ? parseProjectUsers(project) : []));
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggle = email => setChecked(prev => {
    const next = new Set(prev); next.has(email) ? next.delete(email) : next.add(email); return next;
  });

  // ── Layer management ────────────────────────────────────────────────────────
  const [availableR2Layers, setAvailableR2Layers] = useState([]);
  const [r2Layers,          setR2Layers]          = useState(() => parseR2Layers(project?.layers_config));
  const [r2DropOpen,        setR2DropOpen]        = useState(false);
  const r2DropRef = useRef(null);
  const [layerFile,       setLayerFile]       = useState(null);
  const [layerUploading,    setLayerUploading]    = useState(false);
  const [layerUploadDone,   setLayerUploadDone]   = useState(false);
  const [layerUploadErr,    setLayerUploadErr]    = useState("");
  const [layerUploadProgress, setLayerUploadProgress] = useState(0);
  const [editingLayerKey,  setEditingLayerKey]  = useState(null);
  const [editingLayerName, setEditingLayerName] = useState("");
  const [previewLayerKey,  setPreviewLayerKey]  = useState(null);

  useEffect(() => {
    getToken().then(token =>
      fetch(`${API}/api/r2-layers`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { if (d.layers) setAvailableR2Layers(d.layers); })
        .catch(() => {})
    );
    const h = e => { if (r2DropRef.current && !r2DropRef.current.contains(e.target)) setR2DropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleR2Layer = key => setR2Layers(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  const uploadLayer = async () => {
    if (!layerFile) return;
    setLayerUploading(true); setLayerUploadErr(""); setLayerUploadProgress(0);

    const LARGE = 90 * 1024 * 1024; // files > 90 MB go via presigned URL
    try {
      const token = await getToken();

      if (layerFile.size > LARGE) {
        // ── Step 1: get presigned PUT URL ──
        const presignRes = await fetch(`${API}/api/r2-layers?presign=1`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ filename: layerFile.name }),
        });
        let presignData;
        try { presignData = await presignRes.json(); }
        catch { throw new Error(`Server error (${presignRes.status})`); }
        if (!presignRes.ok) throw new Error(presignData.error || "Failed to get upload URL");

        const { presignedUrl, key, name } = presignData;

        // ── Step 2: upload directly to R2 (bypasses Worker size limit) ──
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", presignedUrl);
          xhr.setRequestHeader("Content-Type", layerFile.type || "application/octet-stream");
          xhr.upload.onprogress = e => {
            if (e.lengthComputable) setLayerUploadProgress(Math.round(e.loaded / e.total * 100));
          };
          xhr.onload  = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`R2 error (${xhr.status})`));
          xhr.onerror = () => reject(new Error("Network error — check your connection and try again."));
          xhr.send(layerFile);
        });

        // ── Step 3: register in manifest ──
        await fetch(`${API}/api/r2-layers?register=1`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ key, name }),
        });

        setAvailableR2Layers(prev => [...prev, { key, name, type: "points" }]);
      } else {
        // ── Small file: proxy through Worker ──
        const fd = new FormData();
        fd.append("file", layerFile);
        const res = await fetch(`${API}/api/r2-layers`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
        let data;
        try { data = await res.json(); }
        catch {
          if (res.status === 413) throw new Error("File is too large for the server. Try again — it will use direct upload.");
          throw new Error(`Server error (${res.status}) — the request may have timed out.`);
        }
        if (!res.ok) throw new Error(data.error || "Upload failed");
        setAvailableR2Layers(prev => [...prev, { key: data.key, name: data.name, type: "points" }]);
      }

      setLayerFile(null);
      setLayerUploadProgress(0);
      setLayerUploadDone(true);
      setTimeout(() => setLayerUploadDone(false), 2500);
    } catch (e) {
      setLayerUploadErr(e.message);
      setLayerUploadProgress(0);
    } finally {
      setLayerUploading(false);
    }
  };

  const deleteLayer = async key => {
    const token = await getToken();
    await fetch(`${API}/api/r2-layers?key=${encodeURIComponent(key)}`, {
      method:"DELETE", headers:{ Authorization:`Bearer ${token}` },
    });
    setAvailableR2Layers(prev => prev.filter(l => l.key !== key));
    setR2Layers(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  const renameLayer = async (key, name) => {
    if (!name.trim()) { setEditingLayerKey(null); return; }
    const token = await getToken();
    const res = await fetch(`${API}/api/r2-layers`, {
      method:"PATCH",
      headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
      body: JSON.stringify({ key, name }),
    });
    if (res.ok) setAvailableR2Layers(prev => prev.map(l => l.key === key ? { ...l, name } : l));
    setEditingLayerKey(null);
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.project_name.trim()) { setError("Project name is required"); return; }
    setError(""); setLoading(true);
    try {
      const token = await getToken();
      const layers_config = JSON.stringify({ r2: availableR2Layers.filter(l => r2Layers.has(l.key)) });
      let res;
      if (isEdit) {
        res = await fetch(`${API}/api/updateProject`, {
          method:"PUT",
          headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ id:project.id, ...form, layers_config, user_ids:[...checked] }),
        });
      } else {
        res = await fetch(`${API}/api/createProject`, {
          method:"POST",
          headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
          body: JSON.stringify({ ...form, layers_config, user_ids:[...checked] }),
        });
      }
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `${res.status}`);
      onSuccess();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Modal
      title={isEdit ? "Edit project" : "New project"}
      subtitle={isEdit ? "Update details and user access" : "Create a project and assign users"}
      icon={FolderOpen} onClose={onClose} width={previewLayerKey ? 940 : 520}>
      <div style={{ display:"flex", gap:24, alignItems:"flex-start" }}>
      <div style={{ flex: previewLayerKey ? "0 0 440px" : "1" }}>
      <form onSubmit={submit} style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={lbl}>Project name <span style={{ color:T.danger }}>*</span></label>
          <input style={inp} value={form.project_name} onChange={set("project_name")} autoFocus required />
        </div>
        <div>
          <label style={lbl}>Description</label>
          <textarea style={{ ...inp, minHeight:64, resize:"vertical" }}
            value={form.description} onChange={set("description")} />
        </div>
        {/* ── Layers ── */}
        <div>
          <label style={lbl}>
            <span style={{ display:"flex", alignItems:"center", gap:5 }}>
              <Layers size={11} /> Layers
            </span>
          </label>

          {/* Select layers dropdown */}
          <div ref={r2DropRef} style={{ position:"relative" }}>
            <button type="button" onClick={() => setR2DropOpen(o => !o)}
              style={{ ...inp, display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
              <span style={{ color: r2Layers.size ? T.text : T.muted, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, textAlign:"left" }}>
                {r2Layers.size
                  ? availableR2Layers.filter(l => r2Layers.has(l.key)).map(l => l.name).join(", ")
                  : availableR2Layers.length === 0 ? "No layers uploaded yet" : "Select layers to assign…"}
              </span>
              <ChevronDown size={13} color={T.muted} style={{ flexShrink:0, marginLeft:6, transform: r2DropOpen ? "rotate(180deg)" : "none", transition:"transform 0.15s" }} />
            </button>
            {r2DropOpen && (
              <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:300,
                background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.r,
                boxShadow:T.shadowMd, overflow:"hidden", maxHeight:200, overflowY:"auto" }}>
                {availableR2Layers.length === 0 && (
                  <div style={{ padding:"10px 12px", fontSize:13, color:T.muted, fontFamily:font }}>
                    No layers uploaded yet. Upload a .parquet file below.
                  </div>
                )}
                {availableR2Layers.map(layer => (
                  <div key={layer.key} style={{ display:"flex", alignItems:"center",
                    background: r2Layers.has(layer.key) ? T.accentLight : "transparent",
                    borderBottom:`1px solid #f3f4f6` }}>
                    <div onClick={() => editingLayerKey !== layer.key && toggleR2Layer(layer.key)}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                        flex:1, cursor: editingLayerKey === layer.key ? "default" : "pointer", userSelect:"none" }}>
                      <input type="checkbox" readOnly checked={r2Layers.has(layer.key)}
                        style={{ accentColor:T.accent, pointerEvents:"none", flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        {editingLayerKey === layer.key ? (
                          <input
                            autoFocus
                            value={editingLayerName}
                            onChange={e => setEditingLayerName(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => {
                              if (e.key === "Enter") { e.preventDefault(); renameLayer(layer.key, editingLayerName); }
                              if (e.key === "Escape") setEditingLayerKey(null);
                            }}
                            onBlur={() => renameLayer(layer.key, editingLayerName)}
                            style={{ fontSize:13, fontFamily:font, color:T.text, border:`1px solid ${T.accent}`,
                              borderRadius:4, padding:"2px 6px", outline:"none", width:"100%", boxSizing:"border-box" }}
                          />
                        ) : (
                          <div style={{ fontSize:13, color:T.text, fontWeight:500, fontFamily:font,
                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{layer.name}</div>
                        )}
                        <div style={{ fontSize:11, color:T.muted, marginTop:1, fontFamily:font }}>{layer.key}</div>
                      </div>
                    </div>
                    <button type="button"
                      onClick={e => { e.stopPropagation(); setPreviewLayerKey(k => k === layer.key ? null : layer.key); }}
                      title="Preview on map"
                      style={{ background:"none", border:"none", cursor:"pointer", flexShrink:0,
                        color: previewLayerKey === layer.key ? T.accent : "#d1d5db", padding:"0 6px" }}>
                      <Eye size={12} />
                    </button>
                    <button type="button"
                      onClick={e => { e.stopPropagation(); setEditingLayerKey(layer.key); setEditingLayerName(layer.name); }}
                      title="Rename"
                      style={{ background:"none", border:"none", cursor:"pointer", color:"#d1d5db", padding:"0 6px", flexShrink:0 }}>
                      <Pencil size={12} />
                    </button>
                    <button type="button"
                      onClick={e => { e.stopPropagation(); deleteLayer(layer.key); }}
                      title="Delete from R2"
                      style={{ background:"none", border:"none", cursor:"pointer", color:"#d1d5db", padding:"0 12px", flexShrink:0 }}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload row */}
          <div style={{ display:"flex", gap:8, marginTop:8, alignItems:"center" }}>
            <label style={{ flex:1, display:"flex", alignItems:"center", gap:8, padding:"7px 10px",
              border:`1px solid ${T.border}`, borderRadius:T.r, background:T.surface,
              cursor:"pointer", fontSize:13, color: layerFile ? T.text : T.muted, fontFamily:font, boxSizing:"border-box" }}>
              <Upload size={13} color={T.muted} style={{ flexShrink:0 }} />
              <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {layerFile ? layerFile.name : "Choose .parquet file…"}
              </span>
              <input type="file" accept=".parquet,.csv"
                onChange={e => { setLayerFile(e.target.files[0] || null); setLayerUploadErr(""); setLayerUploadDone(false); }}
                style={{ display:"none" }} />
            </label>
            <button type="button" onClick={uploadLayer}
              disabled={!layerFile || layerUploading || layerUploadDone}
              style={{ ...btnPrimary,
                opacity: (!layerFile || layerUploading) ? 0.5 : 1, flexShrink:0,
                ...(layerUploadDone ? { background:"#22c55e" } : {}) }}>
              {layerUploading ? (layerUploadProgress > 0 ? `${layerUploadProgress}%` : "Uploading…") : layerUploadDone ? "✓ Uploaded" : "Upload"}
            </button>
          </div>
          {layerUploading && layerUploadProgress > 0 && (
            <div style={{ marginTop:6, height:4, borderRadius:2, background:T.border, overflow:"hidden" }}>
              <div style={{ height:"100%", borderRadius:2, background:T.accent, width:`${layerUploadProgress}%`, transition:"width 0.2s" }} />
            </div>
          )}
          {layerUploadErr && (
            <p style={{ margin:"4px 0 0", fontSize:12, color:T.danger, fontFamily:font }}>{layerUploadErr}</p>
          )}
        </div>
        {users.length > 0 && (
          <div>
            <label style={lbl}>Assign users</label>
            <div style={{ border:`1px solid ${T.border}`, borderRadius:T.r, padding:10,
              maxHeight:160, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 }}>
              {users.map(u => (
                <label key={u.email} style={{ display:"flex", alignItems:"center", gap:10,
                  cursor:"pointer", fontSize:13, color:T.text }}>
                  <input type="checkbox" checked={checked.has(u.email)} onChange={() => toggle(u.email)}
                    style={{ accentColor:T.accent, width:14, height:14 }} />
                  <span style={{ flex:1 }}>
                    {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email}
                  </span>
                  <span style={{ fontSize:11, color:T.muted }}>{u.email}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div style={{ padding:"9px 12px", background:T.dangerLight, borderRadius:T.r,
            border:"1px solid #fca5a5", color:T.danger, fontSize:13 }}>{error}</div>
        )}
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", paddingTop:4 }}>
          <button type="button" onClick={onClose} style={btnGhost}>Cancel</button>
          <button type="submit" disabled={loading} style={btnPrimary}>
            {loading ? "Saving…" : isEdit ? "Save changes" : "Create project"}
          </button>
        </div>
      </form>
      </div>
      {previewLayerKey && (
        <div style={{ flex:1, minWidth:0, borderLeft:`1px solid ${T.border}`, paddingLeft:20 }}>
          <LayerPreviewPanel
            layerKey={previewLayerKey}
            layerName={availableR2Layers.find(l => l.key === previewLayerKey)?.name ?? previewLayerKey}
            getToken={getToken}
            dbRef={dbRef}
            dbReady={dbReady}
            onClose={() => setPreviewLayerKey(null)}
          />
        </div>
      )}
      </div>
    </Modal>
  );
}

// ── main ──────────────────────────────────────────────────────────────────────
export default function AdminDashboard({ logout, onOpenAccount }) {
  const { getToken } = useAuth();
  const { user }     = useUser();
  const isMobile     = useIsMobile();
  const { dbRef, ready: dbReady } = useDuckDB();

  const [tab, setTab]           = useState("overview");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const h = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // data
  const [users, setUsers]             = useState([]);
  const [pending, setPending]         = useState([]);
  const [projects, setProjects]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [projLoading, setProjLoading] = useState(false);
  const [userSearch, setUserSearch]   = useState("");

  // demo requests, from the public site's Request a demonstration form
  const [requests, setRequests]     = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqFilter, setReqFilter]   = useState("new");
  const [reqBusy, setReqBusy]       = useState(null);   // id currently being acted on
  const [declineReq, setDeclineReq] = useState(null);   // row awaiting decline confirmation

  // user dialogs
  const [showInvite, setShowInvite] = useState(false);
  const [editUser, setEditUser]     = useState(null);
  const [logsUser, setLogsUser]     = useState(null);
  const [delUser, setDelUser]       = useState(null);
  const [revokeInv, setRevokeInv]   = useState(null);

  // project dialogs
  const [showProjectCreate, setShowProjectCreate] = useState(false);
  const [editProject, setEditProject]             = useState(null);
  const [delProject, setDelProject]               = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/adminUsers`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await res.json();
      if (res.ok) { setUsers(d.users || []); setPending(d.pending_invitations || []); }
    } catch (_) {}
    finally { setLoading(false); }
  }, [getToken]);

  const fetchProjects = useCallback(async () => {
    setProjLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/getProjects`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await res.json();
      if (res.ok) setProjects(d.projects || []);
    } catch (_) {}
    finally { setProjLoading(false); }
  }, [getToken]);

  // ── demo requests ───────────────────────────────────────────────────────────
  // Submitted at geospekter.com/request-demo.html, which posts to the one
  // unauthenticated route, /api/public/demo-request. Reading and acting on them
  // is admin only.
  const fetchRequests = useCallback(async (status = "new") => {
    setReqLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/demo-requests?status=${encodeURIComponent(status)}`,
        { headers:{ Authorization:`Bearer ${token}` } });
      const d = await res.json();
      if (res.ok) setRequests(d.requests || []);
    } catch (_) {}
    finally { setReqLoading(false); }
  }, [getToken]);

  // Approve does what the Invite user button does: writes the address into
  // allowed_emails AND has Clerk send the invitation email. The person then
  // appears under Invitations awaiting sign-up. Both lists are refreshed, or the
  // badge next door goes stale.
  const decideRequest = async (row, action, role = "user") => {
    setReqBusy(row.id);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/demo-requests/${row.id}`, {
        method:"PATCH",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ action, role }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { alert(d.error || "That did not work."); return; }

      // The approval is recorded even when the email fails, so say so plainly.
      // A silent failure here means someone is waiting for a message that is
      // never coming.
      if (action === "approve") {
        if (d.alreadyRegistered) {
          alert(`${d.invited} already has an account, so no invitation was sent. Their role is now ${d.role}.`);
        } else if (d.emailed === false) {
          alert(`Approved, and ${d.invited} is on the allowlist, but the invitation email did NOT go out:\n\n${d.emailError || "unknown error"}\n\nSend it from the Invitations tab with Invite user.`);
        }
        fetchUsers();
      }
      await fetchRequests(reqFilter);
    } catch (_) {
      alert("Could not reach the server.");
    } finally {
      setReqBusy(null);
      setDeclineReq(null);
    }
  };

  useEffect(() => { fetchUsers(); fetchProjects(); }, [fetchUsers, fetchProjects]);
  useEffect(() => { fetchRequests(reqFilter); }, [fetchRequests, reqFilter]);

  const doDelete = async () => {
    const u = delUser; setDelUser(null);
    try {
      const token = await getToken();
      await fetch(`${API}/api/adminUsers`, {
        method:"DELETE",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ userId:u.clerk_id, id:u.id }),
      });
      fetchUsers();
    } catch (_) {}
  };

  const doRevoke = async () => {
    const inv = revokeInv; setRevokeInv(null);
    try {
      const token = await getToken();
      await fetch(`${API}/api/adminUsers`, {
        method:"DELETE",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ inviteEmail: inv.email }),
      });
      fetchUsers();
    } catch (_) {}
  };

  const doDeleteProject = async () => {
    const p = delProject; setDelProject(null);
    try {
      const token = await getToken();
      await fetch(`${API}/api/deleteProject`, {
        method:"DELETE",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ id: p.id }),
      });
      fetchProjects();
    } catch (_) {}
  };

  const displayName = user?.firstName || user?.primaryEmailAddress?.emailAddress?.split("@")[0] || "Admin";
  const userEmail   = user?.primaryEmailAddress?.emailAddress || "";

  const tabs = [
    { id:"overview",    label:"Overview" },
    { id:"users",       label:"Users",       badge: users.length || null },
    { id:"invitations", label:"Invitations", badge: pending.length || null },
    // The badge counts only what is waiting, so it is a number of things to do
    // rather than a total. It disappears when the queue is empty.
    { id:"requests",    label:"Requests",
      badge: (reqFilter === "new" ? requests.length : requests.filter(r => r.status === "new").length) || null },
    { id:"projects",    label:"Projects",    badge: projects.length || null },
  ];

  const filtered = users.filter(u =>
    !userSearch ||
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase())
  );

  // ── parse user_ids from project (getProjects returns comma-separated string after transform) ──
  const getProjectUserCount = p => {
    const raw = p.user_ids ?? "";
    if (!raw) return 0;
    return raw.split(",").map(s => s.trim()).filter(Boolean).length;
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:font }}>

      {/* ── top nav ── */}
      <header style={{ position:"sticky", top:0, zIndex:50,
        background:T.nav, borderBottom:`1px solid ${T.navBorder}` }}>

        {/* row 1 */}
        <div style={{ maxWidth:1200, margin:"0 auto", padding:"0 16px",
          display:"flex", alignItems:"center", height:52, gap:12 }}>

          {/* brand */}
          <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, flexShrink:0 }}>
            <img src="/logo-white.png" alt="GeoSpekter"
              style={{ height:26, width:"auto", display:"block", flexShrink:0 }} />
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", padding:"1px 7px",
              border:"1px solid rgba(255,255,255,0.15)", borderRadius:999 }}>admin</span>
          </div>

          {/* desktop tabs */}
          {!isMobile && (
            <nav style={{ display:"flex", alignItems:"center", gap:2 }}>
              {tabs.map(t => {
                const active = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    display:"flex", alignItems:"center", gap:7,
                    padding:"6px 12px", border:"none", borderRadius:6, cursor:"pointer",
                    fontFamily:font, fontSize:13, fontWeight: active ? 600 : 400,
                    background: active ? "#4d5663" : "transparent",
                    color: active ? T.navActive : T.navText,
                  }}>
                    {t.label}
                    {t.badge ? (
                      <span style={{ background: active ? T.accent : "#4d5663",
                        color:"#fff", fontSize:10, fontWeight:700, minWidth:17, height:17,
                        borderRadius:999, display:"inline-flex", alignItems:"center",
                        justifyContent:"center", padding:"0 5px" }}>
                        {t.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </nav>
          )}

          {/* right */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            {!isMobile && (
              <a href={process.env.REACT_APP_INSPEKT_URL || "#"} target="_blank" rel="noreferrer"
                style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12,
                  color:T.navText, textDecoration:"none",
                  padding:"5px 10px", border:"1px solid #374151", borderRadius:6 }}
                onMouseEnter={e => { e.currentTarget.style.color="#f9fafb"; e.currentTarget.style.borderColor="#6b7280"; }}
                onMouseLeave={e => { e.currentTarget.style.color=T.navText; e.currentTarget.style.borderColor="#4d5663"; }}>
                <ExternalLink size={12} /> InSpekt
              </a>
            )}

            <div style={{ position:"relative" }} ref={userMenuRef}>
              <button onClick={() => setShowUserMenu(v => !v)}
                style={{ display:"flex", alignItems:"center", gap:6, background:"none",
                  border:"none", cursor:"pointer", padding:"4px 6px", borderRadius:6 }}>
                <Avatar name={displayName} size={26} />
                {!isMobile && (
                  <span style={{ fontSize:12, color:"#d1d5db", maxWidth:100,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayName}</span>
                )}
                <ChevronDown size={12} color="#6b7280" />
              </button>
              {showUserMenu && (
                <div style={{ position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:100,
                  background:"#fff", border:`1px solid ${T.border}`, borderRadius:T.r,
                  boxShadow:T.shadowMd, minWidth:190, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px 8px", borderBottom:`1px solid ${T.border}` }}>
                    <p style={{ margin:0, fontSize:12, fontWeight:600, color:T.text }}>{displayName}</p>
                    <p style={{ margin:0, fontSize:11, color:T.muted, marginTop:1 }}>{userEmail}</p>
                  </div>
                  {isMobile && (
                    <a href={process.env.REACT_APP_INSPEKT_URL || "#"} target="_blank" rel="noreferrer"
                      style={{ width:"100%", display:"flex", alignItems:"center", gap:10,
                        padding:"10px 14px", borderBottom:`1px solid ${T.border}`,
                        textDecoration:"none", fontSize:13, color:T.text, fontFamily:font }}>
                      <ExternalLink size={13} color={T.muted} /> Open InSpekt
                    </a>
                  )}
                  <button onClick={() => { setShowUserMenu(false); onOpenAccount?.(); }}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:10,
                      padding:"10px 14px", border:"none", background:"transparent",
                      cursor:"pointer", fontSize:13, color:T.text, fontFamily:font, textAlign:"left" }}>
                    <Settings size={13} color={T.muted} /> Account settings
                  </button>
                  <div style={{ height:1, background:T.border }} />
                  <button onClick={() => { setShowUserMenu(false); logout(); }}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:10,
                      padding:"10px 14px", border:"none", background:"transparent",
                      cursor:"pointer", fontSize:13, color:T.danger, fontFamily:font, textAlign:"left" }}>
                    <LogOut size={13} color={T.danger} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* row 2 (mobile): scrollable tabs */}
        {isMobile && (
          <div style={{ borderTop:`1px solid ${T.navBorder}`, overflowX:"auto",
            WebkitOverflowScrolling:"touch", display:"flex", padding:"0 12px",
            scrollbarWidth:"none", msOverflowStyle:"none" }}>
            {tabs.map(t => {
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  display:"flex", alignItems:"center", gap:6, flexShrink:0,
                  padding:"10px 12px", border:"none", background:"transparent",
                  cursor:"pointer", fontFamily:font, fontSize:13,
                  fontWeight: active ? 600 : 400,
                  color: active ? T.navActive : T.navText,
                  borderBottom: active ? `2px solid ${T.accent}` : "2px solid transparent",
                }}>
                  {t.label}
                  {t.badge ? (
                    <span style={{ background: active ? T.accent : "#4d5663",
                      color:"#fff", fontSize:10, fontWeight:700, minWidth:16, height:16,
                      borderRadius:999, display:"inline-flex", alignItems:"center",
                      justifyContent:"center", padding:"0 4px" }}>
                      {t.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {/* ── page content ── */}
      <div style={{ maxWidth:1200, margin:"0 auto", padding: isMobile ? "20px 14px" : "32px 24px" }}>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            <MetricStrip items={[
              { label:"Total users",     value:users.length,
                sub:`${users.filter(u => u.role === "admin").length} admin${users.filter(u => u.role === "admin").length !== 1 ? "s" : ""}` },
              { label:"Pending invites", value:pending.length, sub:"awaiting sign-up" },
              { label:"Projects",        value:projects.length, sub:"created by you" },
              { label:"Recently active", value:users.filter(u => u.last_login).length, sub:"have logged in" },
            ]} />

            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:20 }}>
              <div>
                <SectionHeader title="Users" actions={
                  <button onClick={() => setTab("users")} style={btnGhost}>View all</button>
                } />
                <Table loading={loading} empty="No users yet."
                  cols={[
                    { key:"user", label:"Name", render:u => (
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <Avatar name={u.first_name || u.email} size={26} />
                        <div>
                          <div style={{ fontWeight:500, color:T.text, fontSize:13 }}>
                            {[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}
                          </div>
                          <div style={{ fontSize:11, color:T.muted }}>{u.email}</div>
                        </div>
                      </div>
                    )},
                    { key:"role", label:"Role", render:u => <RoleBadge role={u.role} /> },
                  ]}
                  rows={users.slice(0, 5).map(u => ({ ...u, _key:u.id }))}
                />
              </div>

              <div>
                <SectionHeader title="Pending invitations" actions={
                  <button onClick={() => setShowInvite(true)} style={btnPrimary}>
                    <Plus size={13} /> Invite
                  </button>
                } />
                {pending.length === 0 ? (
                  <div style={{ background:T.surface, border:`1px solid ${T.border}`,
                    borderRadius:T.rLg, padding:"32px 20px", textAlign:"center" }}>
                    <CheckCircle size={28} color={T.accent} style={{ marginBottom:10 }} />
                    <p style={{ margin:0, fontSize:14, fontWeight:500, color:T.text }}>All caught up</p>
                    <p style={{ margin:"4px 0 0", fontSize:13, color:T.muted }}>No pending invitations.</p>
                  </div>
                ) : (
                  <Table loading={loading} empty=""
                    cols={[
                      { key:"email", label:"Email", render:i => <span style={{ fontWeight:500, color:T.text, fontSize:13 }}>{i.email}</span> },
                      { key:"role",  label:"Role",  render:i => <RoleBadge role={i.role} /> },
                      { key:"status", label:"", render:() => (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, color:T.warn, fontWeight:600 }}>
                          <Clock size={11} /> Pending
                        </span>
                      )},
                    ]}
                    rows={pending.map((i, idx) => ({ ...i, _key:idx }))}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {/* USERS */}
        {tab === "users" && (
          <>
            <SectionHeader
              title="Users"
              desc={`${users.length} total user${users.length !== 1 ? "s" : ""}`}
              actions={<>
                <button onClick={fetchUsers} style={btnGhost}><RefreshCw size={13} /></button>
                <button onClick={() => setShowInvite(true)} style={btnPrimary}>
                  <UserPlus size={13} /> Invite user
                </button>
              </>}
            />
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16,
              background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.r,
              padding:"7px 11px" }}>
              <Search size={14} color={T.muted} />
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                placeholder="Search by name or email…"
                style={{ border:"none", outline:"none", fontSize:13, color:T.text,
                  flex:1, fontFamily:font, background:"transparent" }} />
              {userSearch && (
                <button onClick={() => setUserSearch("")}
                  style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, padding:2, lineHeight:0 }}>
                  <X size={13} />
                </button>
              )}
            </div>
            <Table loading={loading} empty="No users found."
              cols={[
                { key:"user", label:"User", render:u => (
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <Avatar name={u.first_name || u.email} size={28} />
                    <div>
                      <div style={{ fontWeight:500, color:T.text }}>
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}
                      </div>
                      <div style={{ fontSize:12, color:T.muted }}>{u.email}</div>
                    </div>
                  </div>
                )},
                { key:"role",        label:"Role",        render:u => <RoleBadge role={u.role} /> },
                { key:"affiliation", label:"Organisation", render:u => u.affiliation || <span style={{ color:"#d1d5db" }}>—</span> },
                { key:"last_login",  label:"Last login",   render:u => u.last_login
                  ? new Date(u.last_login).toLocaleDateString()
                  : <span style={{ color:"#d1d5db" }}>Never</span> },
                { key:"actions", label:"", render:u => (
                  <div style={{ display:"flex", gap:4, justifyContent:"flex-end" }}>
                    <button onClick={() => setLogsUser(u)} title="Logs"
                      style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:6,
                        padding:"4px 7px", cursor:"pointer", color:T.muted, lineHeight:0 }}>
                      <Eye size={13} />
                    </button>
                    <button onClick={() => setEditUser(u)} title="Edit"
                      style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:6,
                        padding:"4px 7px", cursor:"pointer", color:T.muted, lineHeight:0 }}>
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => setDelUser(u)} title="Delete"
                      style={{ background:"none", border:"1px solid #fca5a5", borderRadius:6,
                        padding:"4px 7px", cursor:"pointer", color:T.danger, lineHeight:0 }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                )},
              ]}
              rows={filtered.map(u => ({ ...u, _key:u.id }))}
            />
          </>
        )}

        {/* INVITATIONS */}
        {tab === "invitations" && (
          <>
            <SectionHeader
              title="Invitations"
              desc="Users who have been invited but haven't signed up yet"
              actions={<>
                <button onClick={fetchUsers} style={btnGhost}><RefreshCw size={13} /></button>
                <button onClick={() => setShowInvite(true)} style={btnPrimary}>
                  <UserPlus size={13} /> Invite user
                </button>
              </>}
            />
            {pending.length === 0 && !loading ? (
              <div style={{ background:T.surface, border:`1px solid ${T.border}`,
                borderRadius:T.rLg, padding:"56px 20px", textAlign:"center" }}>
                <CheckCircle size={36} color={T.accent} style={{ marginBottom:14 }} />
                <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>No pending invitations</p>
                <p style={{ margin:"6px 0 18px", fontSize:13, color:T.muted }}>
                  Everyone you've invited has already signed up.
                </p>
                <button onClick={() => setShowInvite(true)} style={btnPrimary}>
                  <UserPlus size={13} /> Invite someone
                </button>
              </div>
            ) : (
              <Table loading={loading} empty="No pending invitations."
                cols={[
                  { key:"email",       label:"Email",       render:i => <span style={{ fontWeight:500, color:T.text }}>{i.email}</span> },
                  { key:"name",        label:"Name",        render:i => [i.first_name, i.last_name].filter(Boolean).join(" ") || <span style={{ color:"#d1d5db" }}>—</span> },
                  { key:"affiliation", label:"Organisation", render:i => i.affiliation || <span style={{ color:"#d1d5db" }}>—</span> },
                  { key:"role",        label:"Role",        render:i => <RoleBadge role={i.role} /> },
                  { key:"invited_at",  label:"Invited",     render:i => i.invited_at ? new Date(i.invited_at).toLocaleDateString() : "—" },
                  { key:"status",      label:"Status",      render:() => (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:5,
                      fontSize:11, color:T.warn, fontWeight:600 }}>
                      <Clock size={11} /> Awaiting sign-up
                    </span>
                  )},
                  { key:"actions", label:"", render:i => (
                    <button onClick={() => setRevokeInv(i)}
                      style={{ background:"none", border:"1px solid #fca5a5", borderRadius:6,
                        padding:"4px 9px", cursor:"pointer", color:T.danger,
                        display:"inline-flex", alignItems:"center", gap:4,
                        fontSize:12, fontFamily:font }}>
                      <X size={11} /> Revoke
                    </button>
                  )},
                ]}
                rows={pending.map((i, idx) => ({ ...i, _key:idx }))}
              />
            )}
          </>
        )}

        {/* REQUESTS — demo requests from the public website */}
        {tab === "requests" && (
          <>
            <SectionHeader
              title="Requests"
              desc="Demo requests from the website. Approving sends the invitation email and adds them to Invitations."
              actions={<>
                {["new", "approved", "declined", "all"].map(s => (
                  <button key={s} onClick={() => setReqFilter(s)}
                    style={{ ...btnGhost,
                      background: reqFilter === s ? T.accentLight : "transparent",
                      color: reqFilter === s ? T.accentText : T.muted,
                      fontWeight: reqFilter === s ? 600 : 400,
                      textTransform:"capitalize" }}>
                    {s}
                  </button>
                ))}
                <button onClick={() => fetchRequests(reqFilter)} style={btnGhost}><RefreshCw size={13} /></button>
              </>}
            />
            {requests.length === 0 && !reqLoading ? (
              <div style={{ background:T.surface, border:`1px solid ${T.border}`,
                borderRadius:T.rLg, padding:"56px 20px", textAlign:"center" }}>
                <Inbox size={36} color={T.accent} style={{ marginBottom:14 }} />
                <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>
                  {reqFilter === "new" ? "No requests waiting" : "Nothing here"}
                </p>
                <p style={{ margin:"6px 0 0", fontSize:13, color:T.muted }}>
                  Requests arrive from the Request a demonstration form on geospekter.com.
                </p>
              </div>
            ) : (
              /* Every value below was typed by a stranger on the public
                 internet. It is rendered as JSX text, which React escapes, and
                 never through dangerouslySetInnerHTML. Keep it that way. */
              <Table loading={reqLoading} empty="No requests."
                cols={[
                  { key:"who", label:"Name", render:r => (
                    <div>
                      <div style={{ fontWeight:500, color:T.text }}>{r.name}</div>
                      {r.job_title && <div style={{ fontSize:12, color:T.muted }}>{r.job_title}</div>}
                    </div>
                  )},
                  { key:"email", label:"Email", render:r => (
                    <a href={`mailto:${encodeURIComponent(r.email)}`}
                      style={{ color:T.text, textDecoration:"none" }}>{r.email}</a>
                  )},
                  { key:"organisation", label:"Organisation",
                    render:r => r.organisation || <span style={{ color:"#d1d5db" }}>—</span> },
                  { key:"where", label:"From", render:r => {
                    const place = r.country || [r.ip_city, r.ip_country].filter(Boolean).join(", ");
                    return place
                      ? <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                          <Globe size={11} /> {place}
                        </span>
                      : <span style={{ color:"#d1d5db" }}>—</span>;
                  }},
                  { key:"message", label:"Message", render:r => r.message
                    ? <span title={r.message} style={{ display:"inline-block", maxWidth:260,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.message}</span>
                    : <span style={{ color:"#d1d5db" }}>—</span> },
                  { key:"created_at", label:"Received",
                    render:r => r.created_at ? new Date(r.created_at.replace(" ", "T") + "Z").toLocaleDateString() : "—" },
                  { key:"actions", label:"", render:r => {
                    if (r.status !== "new") {
                      return (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11,
                          fontWeight:600, color: r.status === "approved" ? T.accent : T.muted }}>
                          {r.status === "approved" ? <CheckCircle size={11} /> : <X size={11} />}
                          {r.status === "approved" ? "Approved" : "Declined"}
                        </span>
                      );
                    }
                    const busy = reqBusy === r.id;
                    return (
                      <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                        <button disabled={busy} onClick={() => decideRequest(r, "approve")}
                          style={{ ...btnPrimary, opacity: busy ? 0.5 : 1,
                            cursor: busy ? "default" : "pointer", padding:"4px 9px", fontSize:12 }}>
                          <Check size={11} /> Approve
                        </button>
                        <button disabled={busy} onClick={() => setDeclineReq(r)}
                          style={{ background:"none", border:"1px solid #fca5a5", borderRadius:6,
                            padding:"4px 9px", cursor: busy ? "default" : "pointer", color:T.danger,
                            display:"inline-flex", alignItems:"center", gap:4,
                            fontSize:12, fontFamily:font, opacity: busy ? 0.5 : 1 }}>
                          <X size={11} /> Decline
                        </button>
                      </div>
                    );
                  }},
                ]}
                rows={requests.map((r, idx) => ({ ...r, _key: r.id ?? idx }))}
              />
            )}
          </>
        )}

        {/* PROJECTS */}
        {tab === "projects" && (
          <>
            <SectionHeader
              title="Projects"
              desc="Create and manage projects. Layers are served by Ratekon using the layers identifier."
              actions={<>
                <button onClick={fetchProjects} style={btnGhost}><RefreshCw size={13} /></button>
                <button onClick={() => setShowProjectCreate(true)} style={btnPrimary}>
                  <Plus size={13} /> New project
                </button>
              </>}
            />
            {projLoading ? (
              <p style={{ color:T.muted }}>Loading…</p>
            ) : projects.length === 0 ? (
              <div style={{ background:T.surface, border:`1px solid ${T.border}`,
                borderRadius:T.rLg, padding:"56px 20px", textAlign:"center" }}>
                <FolderOpen size={36} color="#d1d5db" style={{ marginBottom:14 }} />
                <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>No projects yet</p>
                <p style={{ margin:"6px 0 18px", fontSize:13, color:T.muted }}>
                  Create a project and assign users to give them access in InSpekt.
                </p>
                <button onClick={() => setShowProjectCreate(true)} style={btnPrimary}>
                  <Plus size={13} /> New project
                </button>
              </div>
            ) : (
              <div style={{ display:"grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))", gap:14 }}>
                {projects.map(p => {
                  const userCount = getProjectUserCount(p);
                  return (
                    <div key={p.id} style={{ background:T.surface, border:`1px solid ${T.border}`,
                      borderRadius:T.rLg, padding:"18px 20px", boxShadow:T.shadow }}>
                      <div style={{ display:"flex", alignItems:"flex-start",
                        justifyContent:"space-between", gap:10, marginBottom:10 }}>
                        <div style={{ width:36, height:36, borderRadius:8, background:T.accentLight,
                          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <FolderOpen size={16} color={T.accent} />
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:11, color:T.muted, background:"#f3f4f6",
                            padding:"3px 9px", borderRadius:999, whiteSpace:"nowrap" }}>
                            {userCount} user{userCount !== 1 ? "s" : ""}
                          </span>
                          <button onClick={() => setEditProject(p)} title="Edit"
                            style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:6,
                              padding:"4px 7px", cursor:"pointer", color:T.muted, lineHeight:0 }}>
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => setDelProject(p)} title="Delete"
                            style={{ background:"none", border:"1px solid #fca5a5", borderRadius:6,
                              padding:"4px 7px", cursor:"pointer", color:T.danger, lineHeight:0 }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <h3 style={{ margin:"0 0 4px", fontSize:14, fontWeight:600, color:T.text }}>{p.name}</h3>
                      {p.description && (
                        <p style={{ margin:"0 0 8px", fontSize:13, color:T.muted, lineHeight:1.5 }}>{p.description}</p>
                      )}
                      {p.layers_config && (() => {
                        try {
                          const cfg = JSON.parse(p.layers_config);
                          const names = (cfg.r2 || []).map(l => l.name).filter(Boolean);
                          if (!names.length) return null;
                          return (
                            <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:4 }}>
                              {names.map(n => (
                                <span key={n} style={{
                                  fontSize:10, color:T.muted,
                                  background:T.bg, border:`1px solid ${T.border}`,
                                  borderRadius:4, padding:"1px 6px",
                                }}>{n}</span>
                              ))}
                            </div>
                          );
                        } catch { return null; }
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── modals ── */}
      {showInvite && (
        <InviteModal getToken={getToken} projects={projects}
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); fetchUsers(); }} />
      )}
      {editUser && (
        <EditUserModal user={editUser} getToken={getToken} projects={projects}
          onClose={() => setEditUser(null)}
          onSuccess={() => { setEditUser(null); fetchUsers(); }} />
      )}
      {logsUser && (
        <AccessLogsModal user={logsUser} getToken={getToken} onClose={() => setLogsUser(null)} />
      )}
      {showProjectCreate && (
        <ProjectModal getToken={getToken} users={users} dbRef={dbRef} dbReady={dbReady}
          onClose={() => setShowProjectCreate(false)}
          onSuccess={() => { setShowProjectCreate(false); fetchProjects(); }} />
      )}
      {editProject && (
        <ProjectModal project={editProject} getToken={getToken} users={users} dbRef={dbRef} dbReady={dbReady}
          onClose={() => setEditProject(null)}
          onSuccess={() => { setEditProject(null); fetchProjects(); }} />
      )}
      {delUser && (
        <ConfirmModal
          title="Delete user"
          body={`Permanently deletes <strong>${delUser.email}</strong> and revokes all access. This cannot be undone.`}
          confirmLabel="Delete user"
          onConfirm={doDelete}
          onClose={() => setDelUser(null)}
        />
      )}
      {/* Decline confirmation.
          This does NOT use ConfirmModal, on purpose. ConfirmModal renders its
          body with dangerouslySetInnerHTML, and the address shown here came
          from a stranger on the public internet. Written as JSX, React escapes
          it. */}
      {declineReq && (
        <Modal title="Decline request" onClose={() => setDeclineReq(null)} width={400}>
          <p style={{ margin:"0 0 22px", fontSize:14, color:T.muted, lineHeight:1.6 }}>
            The request from <strong style={{ color:T.text }}>{declineReq.email}</strong> will
            be marked as declined. No invitation is created and no email is sent.
            The record is kept.
          </p>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button onClick={() => setDeclineReq(null)} style={btnGhost}>Cancel</button>
            <button onClick={() => decideRequest(declineReq, "decline")} style={btnDanger}>Decline</button>
          </div>
        </Modal>
      )}
      {revokeInv && (
        <ConfirmModal
          title="Revoke invitation"
          body={`The invitation for <strong>${revokeInv.email}</strong> will be cancelled. The email link they received will no longer work.`}
          confirmLabel="Revoke"
          onConfirm={doRevoke}
          onClose={() => setRevokeInv(null)}
        />
      )}
      {delProject && (
        <ConfirmModal
          title="Delete project"
          body={`Permanently deletes <strong>${delProject.name}</strong>. Users will lose access in InSpekt. This cannot be undone.`}
          confirmLabel="Delete project"
          onConfirm={doDeleteProject}
          onClose={() => setDelProject(null)}
        />
      )}
    </div>
  );
}
